import React from 'react';
import { Avatar, Box, Tooltip, Text } from '@cowbell/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryParams } from '../../../../../components/providers/QueryParamsProvider';
import { Highlighter } from '../../CmailListing';
import {
  constructAvatarInitials,
  useProfilePhoto,
} from '../../../../../components/notes/AvatarWithInitialsFallBack';
import type {
  MessageInfo,
  MessageInfoChild,
} from '../../../../../api/cmail/cmail.dto';
import { SenderTrustBadge } from './SenderTrustBadge';
import { MeetingSummaryButton } from './MeetingSummaryButton';
import { ApproveSenderModalConfig } from './ApproveSenderModal';
import { Modal } from '../../../../../components/modals/v2/helpers/v2.modal.helpers';
import { approveMessage } from '../../../../../api/cmail.api';
import { queries } from '../../../../../queries';
import { CMAIL_THREAD_QUERY_KEY } from '../../../CmailInbox/useGetCmailMessage';
import useNotifications from '../../../../../_lib/notifications/useNotifications';
import {
  extractEmailAddress,
  senderParts,
} from '../../../utils/emailAddressUtils';
import classes from './MetadataAndActions.module.css';
import { useFirstLoadingImage } from '../../../../../utils/useFirstLoadingImage';
import { useStaffPhotoCandidate } from '../../../../../components/user-avatar/StaffPhotoAvatar';

export const MetadataAndActions = ({
  message,
}: {
  message: MessageInfo | MessageInfoChild;
}) => {
  const queryClient = useQueryClient();
  const { query } = useQueryParams();
  const notifications = useNotifications();

  // Parse extractedData string into extractedDataJSON object
  const extractedDataJSON = React.useMemo(() => {
    if (message.extractedDataJSON) {
      return message.extractedDataJSON;
    }
    if (message.extractedData && typeof message.extractedData === 'string') {
      try {
        return JSON.parse(message.extractedData);
      } catch (error) {
        console.error('Failed to parse extractedData:', error);
        return undefined;
      }
    }
    return undefined;
  }, [message.extractedData, message.extractedDataJSON]);

  // Get validated user status - check new location first, fall back to deprecated top-level property
  const validatedUser = React.useMemo(() => {
    if (!extractedDataJSON) return undefined;
    // Try new nested location first
    if (extractedDataJSON.senderDetails?.userVerified !== undefined) {
      return extractedDataJSON.senderDetails.userVerified;
    }
    // Fall back to deprecated top-level property for backward compatibility
    if ('validatedUser' in extractedDataJSON) {
      return extractedDataJSON.validatedUser as boolean | undefined;
    }
    return undefined;
  }, [extractedDataJSON]);

  const senderInitials = constructAvatarInitials(message.senderName);

  const senderEmail = extractEmailAddress(message.senderEmail);
  const sender = senderParts(message.senderName, message.senderEmail);

  // Get profile photo for the sender
  const { data: senderProfilePhoto } = useProfilePhoto({
    email: senderEmail,
  });

  // The Cowbell picture when the sender has one, else the sender's own image, else initials.
  // Until the staff list is live, senderProfilePhoto is a URL for everyone, so only a load says
  // whether it exists; once it is live, a sender it does not name has no staff URL to probe.
  const staffPhoto = useStaffPhotoCandidate(senderEmail, senderProfilePhoto);
  const avatarSrc = useFirstLoadingImage([staffPhoto, message?.senderImageUrl]);

  // Extract To and CC from headers if available
  const toField = message.headers?.To || message.headers?.to;
  const ccField =
    message.headers?.Cc || message.headers?.cc || message.headers?.CC;

  const handleLowTrustClick = () => {
    if (!message.senderTrustDecision) return;

    Modal.show(ApproveSenderModalConfig, {
      data: {
        senderTrustDecision: message.senderTrustDecision,
        senderEmail,
        senderName: message.senderName,
        onApprove: async () => {
          await approveMessage(message.msgId);
          notifications.success('Sender approved successfully');

          // The trust badge renders from the thread query when the message sits in a thread,
          // and from the detail query otherwise, so both have to refetch.
          await Promise.all([
            queryClient.invalidateQueries(
              queries.cmail.detail(message.msgId).queryKey
            ),
            queryClient.invalidateQueries(CMAIL_THREAD_QUERY_KEY),
          ]);
        },
      },
    });
  };

  const recipients = [
    toField && { label: 'To:', value: toField },
    ccField && { label: 'Cc:', value: ccField },
  ].filter(Boolean) as { label: string; value: string }[];

  // The header's second row, on one line: who sent it and whether they are
  // trusted on the left; who it went to on the right, under the date.
  // Recipients give way first when space runs out; the full list is on hover.
  return (
    <Box className={classes.container}>
      <Avatar src={avatarSrc} alt={senderInitials} className={classes.avatar}>
        {senderInitials}
      </Avatar>
      {sender.name && (
        <Text className={classes.senderName}>
          <Highlighter search={query.search}>{sender.name}</Highlighter>
        </Text>
      )}
      <Text className={sender.name ? classes.address : classes.senderName}>
        <Highlighter search={query.search}>{sender.address}</Highlighter>
      </Text>
      <SenderTrustBadge
        senderTrustDecision={message.senderTrustDecision}
        validatedUser={validatedUser}
        onLowTrustClick={handleLowTrustClick}
      />
      <MeetingSummaryButton message={message} />
      {recipients.length > 0 && (
        <Tooltip
          label={recipients.map((r) => `${r.label} ${r.value}`).join(', ')}
          multiline
          maw="40rem"
        >
          <Text className={classes.recipients}>
            {recipients.map((r) => (
              <span key={r.label} className={classes.recipient}>
                <span className={classes.recipientLabel}>{r.label}</span>{' '}
                <Highlighter search={query.search}>{r.value}</Highlighter>
              </span>
            ))}
          </Text>
        </Tooltip>
      )}
    </Box>
  );
};
